import { _ } from "../../core/polyglot/polyglot.js";
import { columnsIcon, tableListIcon } from "../../core/elements/icons/index.js";
import { guid } from "../../core/helpers/string-util.js";
import { required } from "../../core/widgets/forms/validators.js";
import { router as routerModel } from "@foretagsplatsen/widgetjs";
import { withViewportScrollRetained } from "../../core/helpers/dom-util.js";
import AccountGroupRowTemplate from "../../models/reports/templates/rowTemplates/AccountGroupRowTemplate.js";
import accountingDocument from "../shared/accountingDocument.js";
import AccountIntervalGroupRowTemplate from "../../models/reports/templates/rowTemplates/AccountIntervalGroupRowTemplate.js";
import AlertCollector from "../../core/alerts/AlertCollector.js";
import dialog from "../../core/widgets/dialog.js";
import displaySettingsWidget from "./displaySettingsWidget.js";
import DocumentActionsRenderer from "./visitors/editor/DocumentActionsRenderer.js";
import FormBuilder from "../../core/widgets/forms/FormBuilder.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import jQuery from "jquery";
import KeyFigurePresetService from "../../core/services/KeyFigurePresetService.js";
import OldSpinnerWidget from "../../deprecated/OldSpinnerWidget/OldSpinnerWidget.js"; // eslint-disable-line import-x/no-deprecated -- todo: migrate to new spinner
import popoverActionButtonWidget from "../../core/widgets/popoverActionButtonWidget.js";
import regionWidget from "../../core/widgets/regionWidget.js";
import reportColumnTemplateFactory from "../../models/reports/templates/columnTemplates/reportColumnTemplateFactory.js";
import reportEditorSelection from "./reportEditorSelection.js";
import reportEditorWidget from "./reportEditorWidget.js";
import reportRowTemplateFactory from "../../models/reports/templates/rowTemplates/reportRowTemplateFactory.js";
import ReportService from "./ReportService.js";
import ReportTemplateItemEditorBuilder from "./visitors/editor/ReportTemplateItemEditorBuilder.js";
import responsive from "../../core/responsive.js";
import session from "../../core/session.js";
import TranslatedString from "../../models/TranslatedString.js";
import ValidationResultWidget from "../../core/widgets/ValidationResultWidget.js";

/**
 * Todo: Add JSDoc comment
 */
function reportEditorDocument(spec = {}, my = {}) {
	const that = accountingDocument(spec, my);

	my.reportService = new ReportService({
		company: session.getCompany(),
		period: session.getPeriod(),
		chartOfAccountsId: session.getCurrentChartOfAccountsId(),
	});

	session.onPeriodChange((period) => {
		my.reportService.setPeriod(period);
	});

	that.getReport = function () {
		return my.reportService.getReport();
	};

	my.dirty = false;
	my.itemTemplateSelection = reportEditorSelection.instance();

	my.validationResultCollector = new AlertCollector();

	my.validationResultWidget = new ValidationResultWidget({
		collector: my.validationResultCollector,
		supportInfo: session.getSupportInfo(),
	});

	my.reportEditor = reportEditorWidget();

	my.reportEditor.templateChanged.register(() => {
		my.beDirty();
		my.updateReport();
	});

	my.itemTemplateSelection.selectionChanged.register((selection) => {
		my.reportEditor.updateSelected(selection);
		my.updateActions();
	});

	my.displaySettings = displaySettingsWidget({
		displaySettings: my.reportService
			.getReportTemplate()
			.getDisplaySettings(),
	});

	my.displaySettings.displaySettingsUpdated.register(() => {
		my.beDirty();
		my.updateReport({ expandReport: true });
	});

	my.displaySettingsPicker = popoverActionButtonWidget({
		label: _("Display settings"),
		dataHelpName: "display-settings",
		popoverHeader: _("Display settings"),
		popoverContent: my.displaySettings,
		popoverAction: my.displaySettings.updateDisplaySettings,
	});

	my.warningRegion = regionWidget({
		content: (html) => my.renderWarningsOn(html),
	});

	my.keyFigurePresetService = new KeyFigurePresetService({
		keyFigureService: getKeyFigureService(),
		companyId: session.getCompanyId(),
	});

	that.documentId = "report-editor";

	that.hasPeriodPicker = function () {
		return true;
	};

	that.hasActionsMenu = function () {
		return true;
	};

	that.actions = function () {
		if (!my.isEditorSupported()) {
			return [];
		}

		if (!my.canShowEditingActions()) {
			return [loadingReportActionsRenderer()];
		}

		return [
			templateActionsRenderer(),
			editActionsRenderer(),
			selectionActionsRenderer(),
		];
	};

	that.title = _("Report Editor");
	that.subtitle = _("Add rows, columns, drag & drop");

	that.help = function () {
		return [
			{
				title: _("Report Editor"),
				content: _(
					"The report editor allows the consultant to create templates that will be populated with numbers from the latest import, the period chosen and potential filtering to a specific dimension done by the viewer.",
				),
			},
			{
				title: _("Live data"),
				content: _(
					"To provide a familiar experience, you will be presented with the current numbers based on the period settings made by you.",
				),
			},
			{
				title: _("Properties"),
				content: _(
					"You can set the template name and description here. This information is shown in the report archive.",
				),
				element: "properties",
			},
			{
				title: _("Display settings"),
				content: _(
					"Display settings let you tweak formatting and other settings of the report template.",
				),
				element: "display-settings",
			},
			{
				title: _("Add column"),
				content: _("There are many types of columns to choose from."),
				element: "add-column",
			},
			{
				title: _("Add row"),
				content: _("There are many types of rows to choose from."),
				element: "add-row",
			},
		];
	};

	that.show = async function (reportTemplateId, reset) {
		my.itemTemplateSelection.clear();

		if (reset) {
			my.resetReportTemplate();
			my.removeResetQueryParameter();
		}

		if (my.getReportTemplate().getId() === reportTemplateId) {
			my.updateReport();
			my.updateActions();
			return;
		}

		that.showUpdating();

		await Promise.all([
			my.reportService.fetchReportTemplate(reportTemplateId),
			my.initializeData(),
		]);

		my.getReportTemplate().collapseDynamicGroupRows();

		my.displaySettings.setDisplaySettings(
			my.getReportTemplate().getDisplaySettings(),
		);

		that.showContent();
		my.updateReport({ expandReport: true });
		my.updateActions();
	};

	that.showNewTemplate = async function (reset) {
		that.showUpdating();

		await my.initializeData();

		if (reset) {
			my.resetReportTemplate();
			my.removeResetQueryParameter();
		}

		that.showContent();
		my.updateReport();
		my.updateActions();
	};

	that.actionsVisibilityChanged = function () {
		// The parent's width is not yet updated, wait til the next tick.
		setTimeout(my.reportEditor.resizeReport, 0);
	};

	that.navigationIconModeToggled = function () {
		// The parent's width is not yet updated, wait til the next tick.
		setTimeout(my.reportEditor.resizeReport, 0);
	};

	/**
	 * Remove `item` from the template.  If `item` is a dependency of other
	 * template items, display a confirmation dialog.
	 */
	that.removeItem = function (item) {
		const dependencies = my.getReportTemplate().getDependenciesTo(item);
		if (dependencies.length) {
			my.showDependencyWarningDialog({ item, dependencies });
		} else {
			my.doRemoveItem(item);
		}
	};

	that.updateItem = function (...specs) {
		my.beDirty();
		my.updateReport(...specs);
	};

	that.addItem = function ({
		itemTemplate,
		parent = my.getReportTemplate(),
	}) {
		itemTemplate.onBeforeAdded && itemTemplate.onBeforeAdded();
		parent.add(itemTemplate, my.itemTemplateSelection.get());
		my.updateReport();
		my.beDirty();
		my.scrollToItem(itemTemplate);
	};

	that.editItem = function (itemTemplate) {
		my.showItemEditionDialog({
			itemTemplate,
			action: () => {
				my.beDirty();
				my.updateReport();
			},
		});
	};

	my.renderContentOn = function (html) {
		html.render(my.warningRegion);
		html.render(my.validationResultWidget);
		html.render(my.reportEditor);
	};

	my.renderWarningsOn = function (html) {
		if (!my.isEditorSupported()) {
			my.renderUnsupportedOnMobileOn(html);
			return;
		}

		if (my.hasFiscalYearsWithDifferentChartOfAccounts()) {
			my.renderDifferentChartOfAccountsWarningOn(html);
		}
	};

	my.renderUnsupportedOnMobileOn = function (html) {
		html.div(
			{ klass: "alert alert-warning" },
			_("The report editor is not supported on mobile devices."),
		);
	};

	my.renderDifferentChartOfAccountsWarningOn = function (html) {
		html.div(
			{ klass: "alert alert-warning" },
			_(
				"Custom reports with Account group rows might not display expected numbers because fiscal years use different chart of accounts.",
			),
			(html) => {
				if (
					!session
						.getCurrentUser()
						.canViewCompanySettingsDocument(session.getCompany())
				) {
					return;
				}
				html.br();

				html.a(
					{ href: my.linkTo("company-settings") },
					_("Change chart of accounts"),
				);
			},
		);
	};

	my.hasFiscalYearsWithDifferentChartOfAccounts = function () {
		const template = my.getReportTemplate();

		return (
			template &&
			template.hasAccountGroupRowTemplate() &&
			session.getCompany().hasFiscalYearsWithDifferentChartOfAccounts()
		);
	};

	my.getReportTemplate = function () {
		return my.reportService.getReportTemplate();
	};

	my.resetReportTemplate = function () {
		my.reportService.resetReportTemplate();

		my.displaySettings.setDisplaySettings(
			my.getReportTemplate().getDisplaySettings(),
		);
	};

	my.canShowEditingActions = function () {
		return that.getReport();
	};

	/**
	 * Links to the report editor from the report viewer include a "reset"
	 * query parameter.
	 *
	 * When we match a route with a "reset=true" parameter, make sure to
	 * remove it from the location hash query string, by *replacing* the
	 * current location history (so that we do not mess with the back
	 * button).
	 *
	 * By doing this, we make sure that further query parameter changes
	 * (like changing the current session period) won't trigger a "reset".
	 */
	my.removeResetQueryParameter = function () {
		const router = routerModel.getRouter();
		const parameters = router.getParameters();
		delete parameters.reset;
		const hash = router.expand({ parameters });

		window.history.replaceState(
			{},
			"report-editor",
			`${window.location.pathname}#!${hash}`,
		);
	};

	my.saveTemplate = function (reportTemplate) {
		if (
			!reportTemplate.getTitle() ||
			!reportTemplate.getTitle().getValue()
		) {
			my.saveUnnamedTemplate(reportTemplate);
			return;
		}

		my.reportService.saveReportTemplate({
			reportTemplate,
			callback: () => {
				my.dirty = false;
				my.redirectTo(`report-editor/${reportTemplate.getId()}`);
			},
		});
	};

	my.saveUnnamedTemplate = function (reportTemplate) {
		my.configure({
			title: _("Save new report"),
			formSpec: {
				saveAction: my.saveTemplate,
				model: reportTemplate,
			},
			formBuilder: (form) => {
				form.input({
					label: _("Title"),
					name: "title",
					accessor: () => reportTemplate.getTitleString(),
					mutator: (title) =>
						reportTemplate.setTitle(
							new TranslatedString({ value: title }),
						),
					validator: required(),
				});
			},
		});
	};

	my.saveTemplateAs = function () {
		const reportTemplate = my.getReportTemplate().clone();
		reportTemplate.setTitle("");
		my.saveTemplate(reportTemplate);
	};

	my.restoreDefaultTemplate = function () {
		const templateId = my.getReportTemplate().getId();

		my.confirmDeletion({
			title: _("Restore default report?"),
			body: _(
				"This action cannot be undone. Do you really want to delete the changes done to this report and return to the default report?",
			),
			okLabel: _("Restore"),
			callback: () => {
				my.reportService.removeReportTemplate({
					reportTemplate: my.getReportTemplate(),
					callback: () => {
						my.resetReportTemplate();
						my.redirectTo(`report-editor/${templateId}`);
					},
				});
			},
		});
	};

	my.deleteTemplate = function () {
		my.confirmDeletion({
			name: my.getReportTemplate().getTitle(),
			callback: () =>
				my.reportService.removeReportTemplate({
					reportTemplate: my.getReportTemplate(),
					callback: () => my.redirectTo("customized-reports"),
				}),
		});
	};

	my.beDirty = function () {
		my.dirty = true;
		my.updateActions();
	};

	my.scrollToItem = function (itemTemplate) {
		const element = jQuery(`[reportid=${itemTemplate.getId()}]`);
		const position = element.position();

		if (!position) {
			return;
		}

		if (itemTemplate.isColumn()) {
			jQuery("#viewport").scrollTop(0);
			jQuery("#viewport").scrollLeft(position.left);
		} else {
			jQuery("#viewport").scrollTop(position.top);
			jQuery("#viewport").scrollLeft(0);
		}
	};

	my.editTemplateProperties = function () {
		const form = new FormBuilder({
			model: my.getReportTemplate(),
			labelClass: "col-sm-4",
			inputClass: "col-sm-8",
			showActions: false,
		});

		form.input({
			label: _("Title"),
			accessor: my
				.getReportTemplate()
				.getTitleString.bind(my.getReportTemplate()),
			mutator: (title) =>
				my
					.getReportTemplate()
					.setTitle(new TranslatedString({ value: title })),
		});

		form.textarea({
			label: _("Description"),
			accessor: my
				.getReportTemplate()
				.getDescription.bind(my.getReportTemplate()),
			mutator: my
				.getReportTemplate()
				.setDescription.bind(my.getReportTemplate()),
		});

		form.checkbox({
			label: _("Show period comparison picker"),
			accessor: my
				.getReportTemplate()
				.showPeriodComparisonPicker.bind(my.getReportTemplate()),
			mutator: my
				.getReportTemplate()
				.setShowPeriodComparisonPicker.bind(my.getReportTemplate()),
		});

		my.confirm({
			title: _("Edit {title} properties", {
				title: my.getReportTemplate().getTitleString(),
			}),
			body: form,
			confirmLabel: _("Save"),
			callback: () => {
				form.submit();
				my.beDirty();
				my.updateReport();
			},
		});
	};

	/**
	 * Instanciate a new report based on the current template, fetch
	 * corresponding data from the server and then update the
	 * document.
	 *
	 * @param {boolean} [expandReport] - If truthy, expands every
	 *   row of the report according to the template's default
	 *   expansion level. When falsy, the default, keep the report
	 *   expansion as it will be calculated by the report service
	 * @param {boolean} [keepExpansion] - If truthy, keeps the
	 *   expansion as it is seen on the screen
	 */
	my.updateReport = async function ({
		expandReport = false,
		keepExpansion = false,
	} = {}) {
		my.reportService.updateReport({ keepExpansion });

		if (expandReport) {
			const expansionLevel = my
				.getReportTemplate()
				.getDefaultExpansionLevel();

			that.getReport().expandRowsUpToLevel(expansionLevel);
		}

		my.reportEditor.setReport(that.getReport());
		my.warningRegion.update();

		await my.reportService.updateReportData({ throttle: true });

		// Refresh the whole document instead of just the
		// report editor widget so that the document's
		// renderContentOn has a chance to change the
		// report's language.
		withViewportScrollRetained(() => that.update());

		my.validationResultCollector.updateValidationResults({
			model: my.getReportTemplate(),
		});
	};

	/**
	 * Actually remove `item`.
	 */
	my.doRemoveItem = function (item) {
		item.remove();
		my.itemTemplateSelection.set(null);
		my.beDirty();
		my.updateReport();
	};

	my.showItemEditionDialog = function ({ itemTemplate, action = () => {} }) {
		const builder = new ReportTemplateItemEditorBuilder().visit(
			itemTemplate,
		);
		if (!builder) {
			action(itemTemplate);
			return;
		}

		const form = builder(my.reportService.getInterpretationContext());

		if (form.isEmpty()) {
			action(itemTemplate);
			return;
		}

		const formDialog = dialog({
			header: itemTemplate.getLabel(),
			cssClass: "modal-overflow",
			body: form,
			footer: (html) => {
				html.button(
					{ klass: "btn btn-primary", "data-test": "save" },
					_("Save"),
				).click(() => {
					form.submit();
					if (!form.invalidFields().length) {
						formDialog.hide();
						action(itemTemplate);
					}
				});

				html.button({ klass: "btn btn-default" }, _("Cancel")).click(
					() => formDialog.hide(),
				);
			},
		});

		formDialog.show();
	};

	my.showDependencyWarningDialog = function ({ item, dependencies }) {
		const itemTitle = item.getTitle();

		my.confirm({
			title: _("Remove {item}?", {
				item: itemTitle,
			}),
			body: _(
				'"{item}" is used by {bold: dependencies}. Remove anyway?',
				{
					item: itemTitle,
					bold: () => (html) =>
						html.strong(
							dependencies
								.map((dependency) => dependency.getTitle())
								.join(", "),
						),
				},
			),
			confirmLabel: _("Save"),
			callback: () => my.doRemoveItem(item),
		});
	};

	my.showCreateItemDialog = function ({ itemTemplate, parent }) {
		my.showItemEditionDialog({
			itemTemplate,
			action: () => that.addItem({ itemTemplate, parent }),
		});
	};

	my.redirectToReportViewer = function () {
		my.redirectTo("report-viewer/#id", {
			id: my.getReportTemplate().getId(),
		});
	};

	my.isEditorSupported = function () {
		return !responsive.isMobileBreakpoint();
	};

	my.willDetach = function () {
		my.displaySettingsPicker.willDetach();
	};

	function loadingReportActionsRenderer() {
		// eslint-disable-next-line import-x/no-deprecated -- todo: migrate to new spinner
		return my.rightMenuSection("", new OldSpinnerWidget({ size: 48 }));
	}

	function templateActionsRenderer() {
		const isSavedTemplate = !my.reportService
			.getReportTemplate()
			.isNewTemplate();

		return my.rightMenuSection(_("Manage template"), (html) => {
			if (
				my.reportService.getReportTemplate().isNewTemplate() ||
				my.dirty
			) {
				html.render(
					my.buttonRenderer({
						label: _("Save template"),
						additionalClasses: "btn-info",
						action: () => my.saveTemplate(my.getReportTemplate()),
						dataTestName: "save-template",
					}),
				);
			}

			if (isSavedTemplate && !my.dirty) {
				html.render(
					my.buttonRenderer({
						label: _("View report"),
						additionalClasses: "btn-info",
						action: my.redirectToReportViewer,
						dataTestName: "view-report",
					}),
				);
			}

			html.render(
				my.buttonRenderer({
					label: _("Properties"),
					action: my.editTemplateProperties,
					dataHelpName: "properties",
				}),
			);

			html.render(my.displaySettingsPicker);

			html.render(my.collapsedActionsRenderer(isSavedTemplate));
		});
	}

	function editActionsRenderer() {
		return (html) => {
			html.render(
				my.rightMenuSection(
					"",
					my.addColumnDropdownRenderer(),
					my.addRowDropdownRenderer(my.getParentRowFromSelection()),
					my.addRowDropdownRenderer(my.itemTemplateSelection.get()),
				),
			);
		};
	}

	my.getParentRowFromSelection = function () {
		const selection = my.itemTemplateSelection.get();
		if (!selection) {
			return my.getReportTemplate();
		}

		return selection.getParent();
	};

	my.addColumnDropdownRenderer = function () {
		return my.addDropdownRenderer({
			templateClasses: reportColumnTemplateFactory.allItemClasses(),
			label: _("Add column…"),
			icon: columnsIcon,
			dataTestName: "add-column",
			dataHelpName: "add-column",
		});
	};

	my.addRowDropdownRenderer = function (parent) {
		if (!parent || !parent.canHaveChildren()) {
			return "";
		}

		return my.addDropdownRenderer({
			templateClasses: reportRowTemplateFactory.allItemClasses(),
			parent,
			label: my.addRowLabelRenderer(parent),
			icon: tableListIcon,
			filter: (rowTemplate) => parent.canAdd(rowTemplate),
			dataTestName: "add-row",
			dataHelpName: "add-row",
		});
	};

	my.addRowLabelRenderer = function (parent) {
		return (html) => {
			if (parent.isRow()) {
				html.render(
					_("Add row to {title}…", {
						title: parent.getTitle(),
					}),
				);
			} else {
				html.render(_("Add row…"));
			}
		};
	};

	my.addDropdownRenderer = function ({
		templateClasses,
		parent = my.getReportTemplate(),
		label,
		icon,
		filter = () => true,
		dataTestName,
		dataHelpName,
	}) {
		return (html) => {
			// TODO: Empty selection is broken in dropdownWidget.js?
			const dropdownId = guid();

			html.div(
				{
					klass: "dropdown",
					"data-test": dataTestName,
					"data-help": dataHelpName,
				},
				html.button(
					{
						klass: "btn btn-default dropdown-toggle btn-block",
						id: dropdownId,
						type: "button",
						"data-toggle": "dropdown",
						"aria-haspopup": "true",
						"aria-expanded": "false",
					},
					icon,
					label,
				),
				html.ul(
					{
						klass: "dropdown-menu",
						"aria-labelledby": dropdownId,
					},
					templateClasses
						.map((TemplateClass) => new TemplateClass({}))
						.filter(filter)
						.sort((a, b) => (a.getLabel() < b.getLabel() ? -1 : 1))
						.map((itemTemplate) =>
							html.li(
								html.a(itemTemplate.getLabel()).click(() => {
									my.showCreateItemDialog({
										itemTemplate,
										parent,
									});
								}),
							),
						),
				),
			);
		};
	};

	my.collapsedActionsRenderer = function (isSavedTemplate) {
		return my.collapsedRightMenuBlock((html) => {
			html.render(
				my.buttonRenderer({
					enabled: isSavedTemplate,
					label: _("Save template as..."),
					action: my.saveTemplateAs,
				}),
			);

			if (my.getReportTemplate().canBeDeletedBy(session.getCompany())) {
				html.render(
					my.buttonRenderer({
						enabled: isSavedTemplate,
						label: _("Delete template"),
						action: my.deleteTemplate,
					}),
				);
			}

			if (my.getReportTemplate().canBeRestoredFor(session.getCompany())) {
				html.render(
					my.buttonRenderer({
						enabled: isSavedTemplate,
						label: _("Restore default template"),
						action: my.restoreDefaultTemplate,
						dataTestName: "restore-default",
					}),
				);
			}
		});
	};

	my.buttonRenderer = function ({
		enabled = true,
		label,
		action,
		additionalClasses = "",
		dataTestName,
		dataHelpName,
	}) {
		return (html) => {
			html.a(
				{
					klass: [
						"btn",
						"btn-default",
						"btn-block",
						{ disabled: !enabled },
						additionalClasses,
					],
					"data-test": dataTestName,
					"data-help": dataHelpName,
				},
				label,
			).click(action);
		};
	};

	my.initializeData = async function () {
		await Promise.all([
			my.keyFigurePresetService.getVisibleKeyFigures(),
			my.reportService.fetchChartOfAccountsAndKeyFigures(),
		]);
	};

	function selectionActionsRenderer() {
		return (html) => {
			if (!my.itemTemplateSelection.get()) {
				return;
			}

			html.render(
				new DocumentActionsRenderer({
					dimensionTree: session.getDimensionTree(),
					chartOfAccounts: my.reportService.getChartOfAccounts(),
				}).itemActionsRenderer(my.itemTemplateSelection.get())(that),
			);
		};
	}

	return that;
}

//
// Class extensions
//

AccountGroupRowTemplate.prototype.onBeforAdded = function () {
	this._collapsed = true;
};

AccountIntervalGroupRowTemplate.prototype.onBeforeAdded = function () {
	this._collapsed = true;
};

export default reportEditorDocument;
